import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Banner } from '@/components/Banner'
import { platform } from '@/platform'
import { useMinWidth } from '@/lib/useMediaQuery'
import { useScrollLock } from '@/lib/useScrollLock'
import { Sidebar } from './Sidebar'
import { TabBar } from './TabBar'
import { TopBar } from './TopBar'
import { useOnlineStatus } from './useOnlineStatus'

/** On the web the rail is the navigation at every width — a column when there
 *  is room, a drawer when there is not. The thumb-reach tab bar is for the
 *  Capacitor build, where a thumb really is doing the pointing. */
export function AppShell({ children }: { children: ReactNode }) {
  const online = useOnlineStatus()
  const [navOpen, setNavOpen] = useState(false)
  const location = useLocation()
  const menuButton = useRef<HTMLButtonElement>(null)
  // Above `lg` the rail is a static column and there is nothing to lock — the
  // shell already owns the document's overflow there.
  const wide = useMinWidth('lg')

  // The page behind an open drawer stays put. Without this you push the
  // navigation aside to tap "Patients", the list behind scrolls instead, and
  // closing the drawer leaves you somewhere you never chose to be.
  useScrollLock(navOpen && !wide)

  // Going somewhere closes the drawer. Leaving it open over the screen that was
  // just asked for is the commonest off-canvas bug there is.
  useEffect(() => setNavOpen(false), [location.pathname])

  useEffect(() => {
    if (!navOpen) return
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setNavOpen(false)
      menuButton.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navOpen])

  return (
    <div className="flex min-h-dvh min-w-0 overflow-x-clip bg-canvas lg:h-dvh lg:min-h-0 lg:overflow-hidden">
      {/* Past the rail and the search field, straight to the screen. First in
          the tab order, visible only once it has focus. */}
      <a
        href="#screen"
        className="fixed top-2 left-2 z-skip -translate-y-[400%] rounded-md bg-raised px-4 py-3 font-semibold text-fg no-underline shadow-lg transition-transform duration-150 ease-standard focus-visible:translate-y-0"
      >
        Skip to content
      </a>

      <Sidebar open={navOpen} />

      {navOpen && (
        <button
          type="button"
          className="fixed inset-0 z-scrim border-0 bg-overlay lg:hidden"
          aria-label="Close navigation"
          onClick={() => {
            setNavOpen(false)
            menuButton.current?.focus()
          }}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:h-full lg:overflow-hidden">
        <TopBar
          navOpen={navOpen}
          menuButtonRef={menuButton}
          onToggleNav={() => setNavOpen((open) => !open)}
        />
        {!online && (
          <div className="px-4 pt-3">
            <Banner tone="warning" live>
              You are offline. Anything you type is kept on this device.
            </Banner>
          </div>
        )}
        <div className="min-h-0 flex-1 lg:overflow-y-auto" id="screen">
          {children}
        </div>
        {platform.kind === 'mobile' && <TabBar />}
      </div>
    </div>
  )
}
